import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon, ICONS } from '../../icons';
import { Badge, Btn, Card, MetricCard, Alert, Avatar, FormRow, Field, Modal, SearchBar, FormActions } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import CommitteeService from '../../api/committee';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

function MemberCard({ member: m, catColor, onView, onEdit, onDelete, t }) {
  const statusColor = (m.status === "Active" || m.status === t("status_active")) ? "green" : (m.status === "Former" || m.status === t("status_former")) ? "gray" : "red";
  return (
    <div
      className="bg-white dark:bg-slate-900 rounded-xl2 border border-border dark:border-slate-700/60 shadow-card dark:shadow-slate-900/40 card-hover overflow-hidden"
      style={{ borderTop: `3px solid ${catColor}`, cursor: "pointer" }}
      onClick={onView}
    >
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="relative flex-shrink-0">
            <Avatar name={m.name} size={44} />
            {m.isEmergency && (
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center" style={{ background: "#ef4444" }}>
                <Icon name="phone" size={7} color="white" strokeWidth={2.5} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-ink dark:text-slate-100 truncate">{m.name}</div>
            <div className="text-[11px] text-muted dark:text-slate-400 truncate mb-1.5">{m.position}</div>
            <Badge color={statusColor} dot sm>{t("status_" + m.status.toLowerCase().replace(/ /g, "_")) || m.status}</Badge>
          </div>
        </div>
        <div className="space-y-1 mb-3">
          {m.showFlat && m.flat && (
            <div className="flex items-center gap-1.5">
              <Icon name="building" size={10} color="#94a3b8" />
              <span className="text-[11px] text-slate-500 dark:text-slate-400">{t("text_flat")} {m.flat}</span>
            </div>
          )}
          {m.showPhone && m.phone && m.phone !== "-" && (
            <div className="flex items-center gap-1.5">
              <Icon name="phone" size={10} color="#94a3b8" />
              <span className="text-[11px] text-slate-500 dark:text-slate-400">{m.phone}</span>
            </div>
          )}
          {m.email && m.email !== "-" && (
            <div className="flex items-center gap-1.5">
              <Icon name="mail" size={10} color="#94a3b8" />
              <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{m.email}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-border dark:border-slate-700">
          <Icon name="calendar" size={10} color="#94a3b8" />
          <span className="text-sm text-muted dark:text-slate-400 font-medium">{m.tenureStart} - {m.tenureEnd}</span>
        </div>
        <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
          {m.showPhone && m.phone && m.phone !== "-" && (
            <a href={`tel:${m.phone.replace(/\s/g, "")}`} onClick={e => e.stopPropagation()} className="flex-shrink-0">
              <Btn xs icon="phone" variant="default">{t("btn_call")}</Btn>
            </a>
          )}
          {m.email && m.email !== "-" && (
            <a href={`mailto:${m.email}`} onClick={e => e.stopPropagation()} className="flex-shrink-0">
              <Btn xs icon="mail" variant="outline_primary">{t("btn_email")}</Btn>
            </a>
          )}
          <div className="flex-1" />
          <Btn xs icon="edit" variant="ghost" onClick={e => { e.stopPropagation(); onEdit(); }} />
          <Btn xs icon="trash" variant="danger" onClick={e => { e.stopPropagation(); onDelete(); }} />
        </div>
      </div>
    </div>
  );
}

export default function CommitteeScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.hr?.[key] || translations.en?.hr?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { user } = useAuth();
  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;

  const ICON_NAMES = Object.keys(ICONS);
  const [members, setMembers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("Active");
  const [sortBy, setSortBy] = useState("order");
  const [modal, setModal] = useState(null);
  const [selMember, setSelMember] = useState(null);
  const [selCat, setSelCat] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [saved, setSaved] = useState(false);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, catsRes] = await Promise.all([
        CommitteeService.getMembers(),
        CommitteeService.getCategories()
      ]);
      if (membersRes.success && membersRes.data) {
        setMembers(membersRes.data);
      }
      if (catsRes.success && catsRes.data) {
        setCommittees(catsRes.data);
      }
    } catch (err) {
      if (err.status !== 403) {
        toast.error(t("toast_load_committee_failed"));
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    fetchMembers();
  }, [fetchMembers]);

  const DEF = { name: "", position: "", committee: "Chairman", flat: "", phone: "", email: "", joinDate: "", tenureStart: "", tenureEnd: "", about: "", status: "Active", isEmergency: false, showFlat: true, showPhone: true };
  const [form, setForm] = useState(DEF);
  const [errors, setErrors] = useState({});

  const validateField = (k, v, formData = form) => {
    switch (k) {
      case 'name': {
        const val = (v ?? '').trim();
        if (!val) return t("val_name_required") || "Full Name is required";
        if (/\d/.test(val)) return t("val_name_invalid") || "Full Name cannot contain numbers";
        if (!/^[\p{L}\s.'-]+$/u.test(val)) return t("val_name_invalid") || "Full Name must be a valid name";
        if (val.length < 2) return t("val_name_min") || "Full Name must be at least 2 characters";
        return null;
      }
      case 'position': {
        const val = (v ?? '').trim();
        if (!val) return t("val_position_required") || "Designation / Position is required";
        if (val.length < 2) return "Designation must be at least 2 characters";
        return null;
      }
      case 'committee': {
        const val = (v ?? '').trim();
        if (!val) return t("val_committee_required") || "Committee is required";
        return null;
      }
      case 'email': {
        const val = (v ?? '').trim();
        if (!val) return null;
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) {
          return t("val_email_invalid") || "Please enter a valid email address";
        }
        return null;
      }
      case 'phone': {
        const val = (v ?? '').trim();
        if (!val) return null;
        if (!/^(\+?[0-9\s-]{7,15})$/.test(val) || val.replace(/\D/g, '').length < 7) {
          return t("val_phone_invalid") || "Please enter a valid contact number";
        }
        return null;
      }
      case 'tenureEnd': {
        const start = formData.tenureStart;
        const end = v;
        if (start && end && new Date(end) < new Date(start)) {
          return t("val_tenure_dates_invalid") || "Tenure end date cannot be earlier than tenure start date";
        }
        return null;
      }
      case 'tenureStart': {
        const start = v;
        const end = formData.tenureEnd;
        if (start && end && new Date(end) < new Date(start)) {
          return t("val_tenure_dates_invalid") || "Tenure end date cannot be earlier than tenure start date";
        }
        return null;
      }
      default:
        return null;
    }
  };

  const validateForm = (formData = form) => {
    const errs = {};
    ['name', 'position', 'committee', 'email', 'phone', 'tenureStart', 'tenureEnd'].forEach(field => {
      const err = validateField(field, formData[field], formData);
      if (err) errs[field] = err;
    });
    return errs;
  };

  const sf = k => e => {
    const val = e.target.value;
    const updated = { ...form, [k]: val };
    setForm(updated);

    if (errors[k]) {
      const err = validateField(k, val, updated);
      setErrors(prev => {
        const next = { ...prev };
        if (err) next[k] = err;
        else delete next[k];
        return next;
      });
    }

    if ((k === 'tenureStart' || k === 'tenureEnd') && errors.tenureEnd) {
      const err = validateField('tenureEnd', updated.tenureEnd, updated);
      setErrors(prev => {
        const next = { ...prev };
        if (err) next.tenureEnd = err;
        else delete next.tenureEnd;
        return next;
      });
    }
  };

  const handleBlur = k => () => {
    const err = validateField(k, form[k], form);
    setErrors(prev => {
      const next = { ...prev };
      if (err) next[k] = err;
      else delete next[k];
      return next;
    });
  };

  const sfb = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const DEF_CAT = { name: "", color: "#4f46e5", icon: "users", responsibilities: "", goals: "" };
  const [catForm, setCatForm] = useState(DEF_CAT);
  const sfCat = k => e => setCatForm(f => ({ ...f, [k]: e.target.value }));

  const filtered = members.filter(m => {
    const q = search.toLowerCase();
    const ms = !q || m.name.toLowerCase().includes(q) || m.flat.toLowerCase().includes(q) || m.committee.toLowerCase().includes(q) || m.position.toLowerCase().includes(q);
    const mc = filterCat === "All" || m.committee === filterCat;
    const mv = filterStatus === "All" || m.status === filterStatus || m.status === t("status_" + filterStatus.toLowerCase().replace(/ /g, "_"));
    return ms && mc && mv;
  }).sort((a, b) =>
    sortBy === "name" ? a.name.localeCompare(b.name) :
      sortBy === "newest" ? b.displayOrder - a.displayOrder :
        a.displayOrder - b.displayOrder
  );

  const DEFAULT_CATEGORIES = [
    { id: "Chairman", name: "Chairman", color: "#6366f1", icon: "crown" },
    { id: "Vice Chairman", name: "Vice Chairman", color: "#8b5cf6", icon: "shield" },
    { id: "Secretary", name: "Secretary", color: "#0ea5e9", icon: "file_text" },
    { id: "Joint Secretary", name: "Joint Secretary", color: "#06b6d4", icon: "clipboard" },
    { id: "Treasurer", name: "Treasurer", color: "#10b981", icon: "bank" },
    { id: "Maintenance Committee", name: "Maintenance Committee", color: "#f59e0b", icon: "tool" },
    { id: "Cultural Committee", name: "Cultural Committee", color: "#ec4899", icon: "music" },
    { id: "Security Committee", name: "Security Committee", color: "#ef4444", icon: "shield_check" },
    { id: "Event Committee", name: "Event Committee", color: "#14b8a6", icon: "calendar" },
    { id: "Women's Committee", name: "Women's Committee", color: "#f43f5e", icon: "users" },
    { id: "Sports Committee", name: "Sports Committee", color: "#3b82f6", icon: "activity" },
    { id: "Emergency Response Team", name: "Emergency Response Team", color: "#dc2626", icon: "phone" },
  ];

  const effectiveCommittees = committees.length > 0 ? committees : DEFAULT_CATEGORIES;
  const knownCatIds = new Set(effectiveCommittees.map(c => c.id));
  const unknownCatIds = [...new Set(members.map(m => m.committee))].filter(id => !knownCatIds.has(id));

  const dynamicCategories = [
    ...effectiveCommittees.map(c => ({ id: c.id, color: c.color, icon: c.icon, info: { responsibilities: c.responsibilities, goals: c.goals } })),
    ...unknownCatIds.map(id => ({ id, color: "#64748b", icon: "users", info: null }))
  ];

  const presentCats = dynamicCategories.filter(c =>
    (filterCat === "All" || c.id === filterCat) && filtered.some(m => m.committee === c.id)
  );

  const closeModal = () => { setModal(null); setSelMember(null); setForm(DEF); setErrors({}); setEditCat(null); };

  const save = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErrorMessage = errs.name || errs.position || errs.committee || errs.email || errs.phone || errs.tenureEnd || Object.values(errs)[0];
      toast.error(firstErrorMessage);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        position: form.position.trim(),
        committee: form.committee.trim(),
        email: form.email ? form.email.trim() : null,
        phone: form.phone ? form.phone.trim() : null,
        flat: form.flat ? form.flat.trim() : null,
        about: form.about ? form.about.trim() : null,
      };
      if (!selMember) {
        payload.displayOrder = members.length + 1;
      }

      if (selMember) {
        await CommitteeService.updateMember(selMember.id, payload);
        toast.success(t("toast_member_updated"));
      } else {
        await CommitteeService.createMember(payload);
        toast.success(t("toast_member_added"));
      }

      setSaved(true); setTimeout(() => setSaved(false), 2800);
      closeModal();
      fetchMembers();
    } catch (err) {
      if (err.errors || err.response?.data?.errors) {
        const apiErrors = err.errors || err.response?.data?.errors;
        const mappedErrors = {};
        Object.keys(apiErrors).forEach(key => {
          mappedErrors[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
        });
        setErrors(prev => ({ ...prev, ...mappedErrors }));
        const firstApiError = Object.values(mappedErrors)[0];
        toast.error(firstApiError || t("toast_save_member_failed"));
      } else {
        toast.error(err.message || err.response?.data?.message || t("toast_save_member_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const startEdit = m => { setSelMember(m); setForm({ ...m }); setErrors({}); setModal("edit"); };
  const openDetail = m => { setSelMember(m); setModal("detail"); };
  const openCatDet = c => { setSelCat(c); setModal("cat"); };

  const activeCount = members.filter(m => m.status === "Active" || m.status === t("status_active")).length;
  const formerCount = members.filter(m => m.status === "Former" || m.status === t("status_former")).length;
  const emergencyCount = members.filter(m => m.isEmergency).length;
  const catCount = [...new Set(members.map(m => m.committee))].length;

  if (isMember) {
    return (
      <div className="fade-up flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Icon name="shield" size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-ink dark:text-white">{t("title_access_restricted")}</h2>
          <p className="text-muted dark:text-slate-400">{t("msg_committee_restricted")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-5 pb-28 lg:pb-0">

      {delTarget && (
        <ConfirmModal
          title={t("title_remove_member")}
          message={t("msg_remove_member", { name: delTarget.name })}
          onConfirm={async () => {
            try {
              await CommitteeService.deleteMember(delTarget.id);
              toast.success(t("toast_member_removed"));
              setDelTarget(null);
              fetchMembers();
            } catch (err) {
              toast.error(t("toast_remove_member_failed"));
            }
          }}
          onCancel={() => setDelTarget(null)}
        />
      )}

      {(modal === "add" || modal === "edit") && (
        <Modal
          title={modal === "add" ? t("title_add_member") : t("title_edit_member")}
          onClose={closeModal}
          wide
          footer={
            <FormActions
              onSave={save}
              onCancel={closeModal}
              label={modal === "add" ? t("btn_add_member") : t("btn_save_changes")}
              loading={loading}
              className="mt-0 pt-0 border-0"
            />
          }
        >
          <FormRow cols={2}>
            <Field
              label={t("label_full_name")}
              value={form.name}
              onChange={sf("name")}
              onBlur={handleBlur("name")}
              error={errors.name}
              required
            />
            <Field
              label={t("label_designation")}
              value={form.position}
              onChange={sf("position")}
              onBlur={handleBlur("position")}
              error={errors.position}
              required
            />
          </FormRow>
          <FormRow cols={2}>
            <Field
              label={t("label_committee")}
              type="select"
              value={form.committee}
              onChange={sf("committee")}
              onBlur={handleBlur("committee")}
              error={errors.committee}
              options={dynamicCategories.map(c => ({ value: c.id, label: c.id }))}
              required
            />
            <Field
              label={t("label_status")}
              type="select"
              value={form.status}
              onChange={sf("status")}
              options={["Active", "Former", "Emergency Contact"].map(s => ({ value: s, label: t("status_" + s.toLowerCase().replace(/ /g, "_")) || s }))}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field
              label={t("label_flat_number")}
              value={form.flat}
              onChange={sf("flat")}
              placeholder={t("placeholder_flat")}
            />
            <Field
              label={t("label_contact_number")}
              value={form.phone}
              onChange={sf("phone")}
              onBlur={handleBlur("phone")}
              error={errors.phone}
              placeholder={t("placeholder_contact")}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field
              label={t("label_email_address")}
              type="email"
              value={form.email}
              onChange={sf("email")}
              onBlur={handleBlur("email")}
              error={errors.email}
            />
            <Field
              label={t("label_join_date")}
              type="date"
              value={form.joinDate}
              onChange={sf("joinDate")}
            />
          </FormRow>
          <FormRow cols={2}>
            <Field
              label={t("label_tenure_start")}
              type="date"
              value={form.tenureStart}
              onChange={sf("tenureStart")}
              onBlur={handleBlur("tenureStart")}
              error={errors.tenureStart}
            />
            <Field
              label={t("label_tenure_end")}
              type="date"
              value={form.tenureEnd}
              onChange={sf("tenureEnd")}
              onBlur={handleBlur("tenureEnd")}
              error={errors.tenureEnd}
            />
          </FormRow>
          <Field
            label={t("label_about_bio")}
            type="textarea"
            rows={2}
            value={form.about}
            onChange={sf("about")}
            placeholder={t("placeholder_about_bio")}
          />
          <div className="flex flex-wrap gap-5 py-2">
            {[["isEmergency", t("label_mark_emergency")], ["showFlat", t("label_show_flat")], ["showPhone", t("label_show_phone")]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={form[k]} onChange={sfb(k)} className="w-4 h-4 rounded border-border dark:border-slate-700 accent-primary" />
                <span className="text-sm font-medium text-ink">{l}</span>
              </label>
            ))}
          </div>
        </Modal>
      )}

      {modal === "detail" && selMember && (
        <Modal title={t("title_member_profile")} onClose={closeModal}>
          <div className="space-y-3.5">
            {/* Header Member Profile Card */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50/80 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
              <div className="relative flex-shrink-0">
                <Avatar name={selMember.name} size={64} />
                {selMember.isEmergency && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-800 flex items-center justify-center bg-red-500 shadow-xs">
                    <Icon name="phone" size={9} color="white" strokeWidth={2.5} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-ink dark:text-white truncate">{selMember.name}</div>
                <div className="text-xs text-muted dark:text-slate-400 mb-2 font-medium">{selMember.position}</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge color={(selMember.status === "Active" || selMember.status === t("status_active")) ? "green" : (selMember.status === "Former" || selMember.status === t("status_former")) ? "gray" : "red"} dot sm>
                    {t("status_" + selMember.status.toLowerCase().replace(/ /g, "_")) || selMember.status}
                  </Badge>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/60">
                    {selMember.committee}
                  </span>
                  {selMember.isEmergency && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200/60 dark:border-red-800/60">
                      {t("badge_emergency_contact")}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Information Grid Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {selMember.showFlat && selMember.flat && (
                <div className="bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="building" size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t("label_flat_number")}</span>
                  </div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100">{selMember.flat}</div>
                </div>
              )}

              {selMember.showPhone && selMember.phone && selMember.phone !== "-" && (
                <div className="bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="phone" size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t("label_phone")}</span>
                  </div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100">{selMember.phone}</div>
                </div>
              )}

              {selMember.email && selMember.email !== "-" && (
                <div className="bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="mail" size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t("label_email")}</span>
                  </div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100 truncate">{selMember.email}</div>
                </div>
              )}

              {selMember.joinDate && (
                <div className="bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="calendar" size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t("label_join_date")}</span>
                  </div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100">{selMember.joinDate}</div>
                </div>
              )}

              {(selMember.tenureStart || selMember.tenureEnd) && (
                <div className="sm:col-span-2 bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="activity" size={12} className="text-slate-400" />
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t("label_tenure_period")}</span>
                  </div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100">
                    {selMember.tenureStart || "-"} — {selMember.tenureEnd || "-"}
                  </div>
                </div>
              )}
            </div>

            {/* About Section */}
            {selMember.about && (
              <div className="bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3.5 border border-slate-100 dark:border-slate-800">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{t("label_about")}</div>
                <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-normal">{selMember.about}</div>
              </div>
            )}

            {/* Action Buttons Row */}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              {selMember.showPhone && selMember.phone && selMember.phone !== "-" && (
                <a href={`tel:${selMember.phone.replace(/\s/g, "")}`} className="flex-1">
                  <button type="button" className="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 text-ink dark:text-slate-200 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors cursor-pointer">
                    <Icon name="phone" size={13} className="text-slate-500" />
                    <span>{t("btn_call")}</span>
                  </button>
                </a>
              )}
              {selMember.showPhone && selMember.phone && selMember.phone !== "-" && (
                <a href={`https://wa.me/91${selMember.phone.replace(/\s/g, "")}`} className="flex-1" target="_blank" rel="noopener noreferrer">
                  <button type="button" className="w-full py-2 px-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors cursor-pointer">
                    <span>{t("btn_whatsapp")}</span>
                  </button>
                </a>
              )}
              {selMember.email && selMember.email !== "-" && (
                <a href={`mailto:${selMember.email}`} className="flex-1">
                  <button type="button" className="w-full py-2 px-3 rounded-xl border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/50 dark:bg-indigo-950/30 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors cursor-pointer">
                    <Icon name="mail" size={13} className="text-indigo-500" />
                    <span>{t("btn_email")}</span>
                  </button>
                </a>
              )}
            </div>
          </div>
        </Modal>
      )}

      {modal === "cat" && selCat && (() => {
        const info = selCat.info || { responsibilities: [], goals: "" };
        const catMembers = members.filter(m => m.committee === selCat.id && (m.status === "Active" || m.status === t("status_active")));
        return (
          <Modal title={selCat.id} onClose={() => { setSelCat(null); setModal(null); }} wide>
            <div className="space-y-5">
              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700" style={{ background: selCat.color + "12", borderColor: selCat.color + "25" }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: selCat.color }}>
                  <Icon name={selCat.icon} size={20} color="white" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-bold text-ink dark:text-slate-100 text-base mb-1">{selCat.id}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{info.goals}</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-ink dark:text-slate-100 uppercase tracking-widest mb-3">{t("title_key_responsibilities")}</div>
                <div className="space-y-2">
                  {(info.responsibilities || []).map((r, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: selCat.color + "18" }}>
                        <Icon name="check" size={10} color={selCat.color} strokeWidth={2.5} />
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-300">{r}</span>
                    </div>
                  ))}
                </div>
              </div>
              {catMembers.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-ink dark:text-slate-100 uppercase tracking-widest mb-3">
                    {t("title_current_members")} <span className="text-muted dark:text-slate-400 font-normal normal-case tracking-normal">({catMembers.length})</span>
                  </div>
                  <div className="space-y-2">
                    {catMembers.map(m => (
                      <div key={m.id} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <Avatar name={m.name} size={36} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-ink dark:text-slate-100">{m.name}</div>
                          <div className="text-xs text-muted dark:text-slate-400">{m.position}{m.showFlat && m.flat ? ` · ${t("text_flat")} ${m.flat}` : ""}</div>
                        </div>
                        {m.isEmergency && <Badge color="red" sm>{t("badge_emergency")}</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {modal === "manage-cats" && !editCat && (
        <Modal title={t("title_manage_categories")} onClose={closeModal} wide>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-ink">{t("title_categories")} ({committees.length})</h3>
            <Btn sm variant="primary" icon="plus" onClick={() => { setEditCat('new'); setCatForm(DEF_CAT); }}>{t("btn_add_category")}</Btn>
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
            {committees.map(c => (
              <div key={c.name} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: c.color }}>
                    <Icon name={c.icon} size={14} color="white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-ink dark:text-slate-100">{c.name}</div>
                    <div className="text-xs text-muted dark:text-slate-400 truncate max-w-xs">{c.goals}</div>
                  </div>
                </div>
                <Btn sm variant="secondary" onClick={() => { setEditCat(c); setCatForm({ ...c, responsibilities: (c.responsibilities || []).join('\n') }); }}>{t("btn_edit")}</Btn>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {modal === "manage-cats" && editCat && (
        <Modal title={editCat === "new" ? t("title_add_category") : t("title_edit_category")} onClose={() => setEditCat(null)} wide>
          <FormRow cols={2}>
            <Field label={t("label_category_name")} value={catForm.name} onChange={sfCat("name")} required disabled={editCat !== 'new'} />
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <label className="block text-xs font-bold text-ink uppercase tracking-widest mb-1.5 opacity-80">{t("label_icon")}</label>
                <div
                  className="flex items-center gap-3 px-3 h-[38px] rounded-lg border border-border dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={() => { setShowIconPicker(!showIconPicker); setIconSearch(""); }}
                >
                  <Icon name={catForm.icon || "users"} size={16} className="text-primary" />
                  <span className="text-sm text-ink dark:text-slate-100">{catForm.icon || t("placeholder_select_icon")}</span>
                  <Icon name="chevron_down" size={12} className="ml-auto text-muted" />
                </div>
                {showIconPicker && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-border dark:border-slate-700 rounded-xl shadow-lg z-50 p-3 flex flex-col max-h-72">
                    <input
                      type="text"
                      placeholder={t("placeholder_search_icons")}
                      className="w-full mb-3 px-3 py-1.5 border border-border dark:border-slate-700 rounded-lg text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary flex-shrink-0 bg-white dark:bg-slate-800"
                      value={iconSearch}
                      onChange={e => setIconSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="grid grid-cols-6 gap-2 overflow-y-auto pr-1">
                      {ICON_NAMES.filter(i => i.toLowerCase().includes(iconSearch.toLowerCase())).map(icon => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => { sfCat("icon")({ target: { value: icon } }); setShowIconPicker(false); }}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${catForm.icon === icon ? 'bg-primary text-white' : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                          title={icon}
                        >
                          <Icon name={icon} size={14} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="block text-xs font-bold text-ink dark:text-slate-100 uppercase tracking-widest mb-1.5 opacity-80">{t("label_color")}</label>
                
                {/* Single Selected Color Trigger Button */}
                <div
                  className="flex items-center justify-center h-[38px] w-[58px] rounded-lg border border-border dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  onClick={() => { setShowColorPicker(!showColorPicker); setShowIconPicker(false); }}
                >
                  <div
                    className="w-5 h-5 rounded-full shadow-xs border border-black/10 dark:border-white/20"
                    style={{ backgroundColor: catForm.color || "#6366f1" }}
                  />
                </div>

                {/* Floating Color Options Card Popover */}
                {showColorPicker && (
                  <div className="absolute top-full right-0 mt-1.5 w-56 bg-white dark:bg-slate-900 border border-border dark:border-slate-700 rounded-xl shadow-xl z-50 p-3">
                    <div className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2">Preset Colors</div>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { hex: "#6366f1", name: "Indigo" },
                        { hex: "#8b5cf6", name: "Purple" },
                        { hex: "#0ea5e9", name: "Sky" },
                        { hex: "#10b981", name: "Emerald" },
                        { hex: "#f59e0b", name: "Amber" },
                        { hex: "#ef4444", name: "Rose" },
                        { hex: "#ec4899", name: "Pink" },
                        { hex: "#14b8a6", name: "Teal" },
                        { hex: "#3b82f6", name: "Blue" },
                        { hex: "#84cc16", name: "Lime" },
                        { hex: "#64748b", name: "Slate" },
                        { hex: "#0f172a", name: "Dark" },
                      ].map(c => (
                        <button
                          key={c.hex}
                          type="button"
                          onClick={() => {
                            sfCat("color")({ target: { value: c.hex } });
                            setShowColorPicker(false);
                          }}
                          className={`w-9 h-9 rounded-lg transition-transform flex items-center justify-center cursor-pointer border ${
                            (catForm.color || "").toLowerCase() === c.hex.toLowerCase()
                              ? "scale-105 border-white ring-2 ring-primary shadow-xs"
                              : "border-transparent hover:scale-105 opacity-90 hover:opacity-100"
                          }`}
                          style={{ backgroundColor: c.hex }}
                          title={c.name}
                        >
                          {(catForm.color || "").toLowerCase() === c.hex.toLowerCase() && (
                            <Icon name="check" size={12} color="white" strokeWidth={3} />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </FormRow>
          <Field label={t("label_goals_objective")} type="textarea" rows={2} value={catForm.goals} onChange={sfCat("goals")} placeholder={t("placeholder_goals")} />
          <Field label={t("label_responsibilities_list")} type="textarea" rows={4} value={catForm.responsibilities} onChange={sfCat("responsibilities")} placeholder={t("placeholder_responsibilities")} />
          <FormActions
            onSave={async () => {
              if (!catForm.name.trim()) return toast.error(t("toast_category_name_required"));
              setLoading(true);
              try {
                const payload = {
                  ...catForm,
                  responsibilities: catForm.responsibilities ? catForm.responsibilities.split('\n').map(s => s.trim()).filter(Boolean) : []
                };
                if (editCat === 'new') {
                  await CommitteeService.createCategory(payload);
                  toast.success(t("toast_category_created"));
                } else {
                  await CommitteeService.updateCategory(catForm.name, payload);
                  toast.success(t("toast_category_updated"));
                }
                setEditCat(null);
                fetchMembers();
              } catch (err) {
                toast.error(err.response?.data?.message || t("toast_save_category_failed"));
              } finally {
                setLoading(false);
              }
            }}
            onCancel={() => setEditCat(null)}
            label={t("btn_save_category")}
            loading={loading}
          />
        </Modal>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label={t("metric_total_members")} value={members.length} icon="users" accent="blue" />
        <MetricCard label={t("metric_active_members")} value={activeCount} icon="check" accent="green" />
        <MetricCard label={t("metric_former_members")} value={formerCount} icon="activity" accent="amber" />
        <MetricCard label={t("metric_emergency_contacts")} value={emergencyCount} icon="siren" accent="red" />
      </div>

      {saved && <Alert type="success" onClose={() => setSaved(false)}>{t("alert_member_saved")}</Alert>}

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <SearchBar value={search} onChange={setSearch} placeholder={t("placeholder_search_member")} className="flex-1 min-w-48" />
          <div className="flex items-center gap-1.5">
            <Icon name="filter" size={13} className="text-muted" />
            <select
              value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="border border-border dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink dark:text-slate-100 bg-white dark:bg-slate-800 outline-none cursor-pointer"
            >
              <option value="All">{t("opt_all_committees")} ({dynamicCategories.length})</option>
              {dynamicCategories.map(c => <option key={c.id} value={c.id}>{c.id}</option>)}
            </select>
          </div>
          <div className="flex rounded-lg border border-border dark:border-slate-700 overflow-hidden text-xs font-semibold">
            {["Active", "Former", "All"].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 border-0 cursor-pointer transition-colors ${filterStatus === s ? "bg-primary text-white" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"}`}>
                {s}
              </button>
            ))}
          </div>
          <select
            value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="border border-border dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink dark:text-slate-100 bg-white dark:bg-slate-800 outline-none cursor-pointer"
          >
            <option value="order">{t("sort_default")}</option>
            <option value="name">{t("sort_name")}</option>
            <option value="newest">{t("sort_newest")}</option>
          </select>
          <Btn sm variant="outline_primary" icon="settings" onClick={() => { setEditCat(null); setModal("manage-cats"); }}>{t("btn_manage_categories")}</Btn>
          <Btn sm variant="primary" icon="plus" className="hidden lg:flex" onClick={() => { setSelMember(null); setForm(DEF); setModal("add"); }}>{t("btn_add_member")}</Btn>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-7">
          {Array(2).fill(0).map((_, i) => (
            <div key={`skel-cat-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-slate-200 dark:bg-slate-700 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 animate-pulse" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/6 animate-pulse" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array(3).fill(0).map((_, j) => (
                  <div key={`skel-card-${j}`} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 h-32 animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : presentCats.length === 0 ? (
        <Card>
          <div className="py-14 flex flex-col items-center gap-3 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Icon name="users" size={22} color="#94a3b8" />
            </div>
            <div className="text-sm font-semibold text-muted dark:text-slate-300">{t("msg_no_members")}</div>
            <div className="text-xs text-slate-400 dark:text-slate-500">{t("msg_adjust_search")}</div>
          </div>
        </Card>
      ) : (
        <div className="space-y-7">
          {presentCats.map(cat => {
            const catMembers = filtered.filter(m => m.committee === cat.id);
            return (
              <div key={cat.id}>
                <div className="flex items-center gap-3 mb-3 group cursor-pointer" onClick={() => openCatDet(cat)}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: cat.color }}>
                    <Icon name={cat.icon} size={16} color="white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-ink dark:text-slate-100 group-hover:text-primary transition-colors">{cat.id}</div>
                    <div className="text-[11px] text-muted dark:text-slate-400">{catMembers.length} {catMembers.length === 1 ? t("text_member_lc") : t("text_members_lc")}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-muted dark:text-slate-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="eye" size={11} /> {t("btn_view_details")}
                  </div>
                  <Icon name="chevron_right" size={14} className="text-muted dark:text-slate-400" />
                </div>
                {/* Desktop View */}
                <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {catMembers.map(m => (
                    <MemberCard
                      key={m.id}
                      member={m}
                      catColor={cat.color}
                      onView={() => openDetail(m)}
                      onEdit={() => startEdit(m)}
                      onDelete={() => setDelTarget(m)}
                      t={t} />
                  ))}
                </div>

                {/* Mobile View */}
                <div className="block lg:hidden space-y-3 mt-3">
                  {catMembers.map((m, i) => (
                    <IonItemSliding key={m.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 && presentCats.indexOf(cat) === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                      <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <div className="flex w-full items-center gap-3 p-3 bg-white dark:bg-slate-900" onClick={() => openDetail(m)}>
                          <div className="relative flex-shrink-0">
                            <Avatar name={m.name} size={42} />
                            {m.isEmergency && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center" style={{ background: "#ef4444" }}>
                                <Icon name="phone" size={7} color="white" strokeWidth={2.5} />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-ink truncate mb-0.5">{m.name}</div>
                            <div className="text-[11px] text-muted truncate">{m.position}</div>
                          </div>

                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <Badge color={(m.status === "Active" || m.status === t("status_active")) ? "green" : (m.status === "Former" || m.status === t("status_former")) ? "gray" : "red"} dot>{t("status_" + m.status.toLowerCase().replace(/ /g, "_")) || m.status}</Badge>
                          </div>

                          <div className="text-slate-300 ml-1">
                            <Icon name="chevron_left" size={14} />
                          </div>
                        </div>
                      </IonItem>

                      <IonItemOptions side="end">
                        <IonItemOption color="primary" onClick={() => startEdit(m)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="edit" size={20} color="white" />
                          </div>
                        </IonItemOption>
                        <IonItemOption color="danger" onClick={() => setDelTarget(m)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="trash" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      </IonItemOptions>
                    </IonItemSliding>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAB for Mobile */}
      {!modal && !delTarget && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => { setSelMember(null); setForm(DEF); setModal("add"); }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
